import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { FilterService } from 'primeng/api';
import { endOfDay, isWithinInterval, startOfDay } from 'date-fns';
import { TypographyComponent } from '../typography.component';
import { StatusOption, TableColumn } from '@/types/table.interface';
import { SortEvent } from 'primeng/api';
@Component({
  selector: 'table-component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    PaginatorModule,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    DatePickerModule,
    TypographyComponent
  ],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent<T extends object> {
  private filterService = inject(FilterService);

  @Input({ required: true }) value: T[] = [];
  @Input({ required: true }) columns: TableColumn<T>[] = [];
  @Input() rows = 10;
  @Input() globalFilterFields: string[] = [];
  @Input() showHeaderFilters = true;
  @Input() paginator = true;
  @Input() showDateFilter = false;
  @Input() statusOptions: StatusOption[] = [];
  @Input() selectedStatus: string | null = null;
  @Input() searchTerm = '';
  @Input() loading = false;
  @Input() totalRecords = 0;
  @Input() isAdmin = false;
  @Input() lazy: boolean = false;


  @Input() actionTemplate?: TemplateRef<{ $implicit: T }>;
  @Input() headerCellTemplate?: TemplateRef<{ $implicit: TableColumn<T> }>;
  @Input() bodyCellTemplate?: TemplateRef<{ $implicit: T, col: TableColumn<T>, rowIndex: number }>;
  @Input() currentPage: number = 1;

  @Output() rowClicked = new EventEmitter<T>();
  @Output() onFilterChange = new EventEmitter<{ status: string | null }>();
  @Output() onClearSearch = new EventEmitter<void>();
  @Output() onSearch = new EventEmitter<string>();
  @Output() onPage = new EventEmitter<PaginatorState>();
  @Output() onSort = new EventEmitter<SortEvent>();
  @Output() onLazyLoad = new EventEmitter<TableLazyLoadEvent>();

  @ViewChild('dt') dt!: Table;

  rangeDates: Date[] | undefined;
  first = 0;

  onDateFilter(): void {
    if (this.rangeDates && this.rangeDates[0] && this.rangeDates[1]) {
      const filterName = 'customDateRange';
      this.filterService.register(filterName, (value: any, filter: Date[]): boolean => {
        if (!filter || filter.length < 2 || !value) return true;
        const dateValue = new Date(value);
        const start = startOfDay(filter[0]);
        const end = endOfDay(filter[1]);
        return isWithinInterval(dateValue, { start, end });
      });
      this.dt.filter(this.rangeDates, 'date', filterName);
    }
  }

  clearDateFilter(): void {
    this.rangeDates = undefined;
    if (this.dt) {
      this.dt.filter(null, 'date', 'equals');
    }
  }

  onPageChange(event: PaginatorState): void {
  this.first = event.first ?? 0;
  this.onPage.emit(event);
}

  globalSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.onSearch.emit(input.value);
  }

  clearSearch(): void {
    this.onClearSearch.emit();
  }


 onStatusChange(value: string | null): void {
    if (this.dt) {
      this.dt.filters = {};
    }
    this.onFilterChange.emit({ status: value });
  }

  clear(): void {
    this.rangeDates = undefined;
    this.first = 0;
    if (this.dt) {
      this.dt.clear();
    }
  }

  onSortChange(event: SortEvent): void {
    this.onSort.emit(event);
  }

  onLazyLoadHandler(event: TableLazyLoadEvent): void {
    this.onLazyLoad.emit(event);
  }
}
